import React, { useState, useEffect } from 'react';
import { Facebook, Instagram, Youtube, Video, Briefcase, Linkedin, Twitter, Pin, Plus, RefreshCw, Trash2, AlertCircle, CheckCircle, XCircle } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { getUserApiCredentials } from '../../lib/apiCredentials';
import toast from 'react-hot-toast';
import { InstagramDirectLinking } from './InstagramDirectLinking';

interface SocialAccount {
  id: string;
  platform: 'facebook' | 'instagram' | 'instagram_direct' | 'youtube' | 'tiktok' | 'tiktok_business' | 'linkedin' | 'x' | 'pinterest';
  page_id: string | null;
  ig_business_id: string | null;
  youtube_channel_id: string | null;
  page_name: string;
  status: 'active' | 'expired' | 'error';
  permissions: string[];
  created_at: string;
  polling_interval_minutes?: number;
  quota_usage_daily?: number;
  last_quota_reset?: string;
  token_expires_at?: string | null;
  meta_token_expires_at?: string | null;
  last_token_check?: string | null;
  account_data?: {
    token_check?: {
      checked_at?: string;
      result?: 'valid' | 'invalid';
      error_code?: number | null;
      error_message?: string | null;
    };
  } | null;
  social_settings?: {
    id: string;
    auto_publish_default_ready: boolean;
    auto_comment_enabled: boolean;
  };
}

export function SocialAccounts() {
  const [accounts, setAccounts] = useState<SocialAccount[]>([]);
  const [loading, setLoading] = useState(true);
  const [connecting, setConnecting] = useState(false);

  useEffect(() => {
    loadAccounts();
    reportOAuthResult();
  }, []);

  // Powrot z OAuth laduje tutaj z ?success= albo ?error=. Bez tego uzytkownik nie widzi,
  // dlaczego laczenie konta sie nie udalo.
  const reportOAuthResult = () => {
    const params = new URLSearchParams(window.location.search);
    const success = params.get('success');
    const error = params.get('error');

    if (!success && !error) return;

    if (error) {
      const description = params.get('error_description');
      const messages: Record<string, string> = {
        no_organizations: 'LinkedIn nie zwrócił żadnej strony firmowej, którą administrujesz.',
        no_credentials: 'Brakuje kluczy LinkedIn Social. Dodaj je w ustawieniach API.',
        invalid_state: 'Autoryzacja wygasła lub została przerwana. Spróbuj jeszcze raz.',
        token_exchange_failed: 'LinkedIn odrzucił wymianę kodu na token.',
      };
      toast.error(messages[error] || description || `Połączenie z LinkedIn nie powiodło się (${error})`);
    } else {
      toast.success('Konto LinkedIn zostało połączone');
    }

    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const loadAccounts = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data, error } = await supabase
        .from('social_accounts')
        .select(`
          *,
          social_settings (
            id,
            auto_publish_default_ready,
            auto_comment_enabled
          )
        `)
        .eq('owner_user_id', user.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setAccounts(data || []);
    } catch (error) {
      console.error('Error loading accounts:', error);
      toast.error('Nie udało się załadować kont');
    } finally {
      setLoading(false);
    }
  };

  const handleConnectMeta = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const appId = credentials.facebook_app_id;

      if (!appId) {
        toast.error('Facebook App ID nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      const redirectUri = 'https://sms.aiagenthub.pl/auth/meta/callback';

      const scopes = [
        'pages_show_list',
        'pages_messaging',
        'pages_read_engagement',
        'pages_read_user_content',
        'pages_manage_engagement',
        'pages_manage_metadata',
        'pages_manage_posts',
        'pages_manage_ads',
        'ads_management',
        'business_management',
        'instagram_basic',
        'instagram_manage_messages',
        'instagram_manage_comments',
      ];

      localStorage.setItem('social_auth_redirect', window.location.href);

      const authUrl = new URL('https://www.facebook.com/v21.0/dialog/oauth');
      authUrl.searchParams.set('client_id', appId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('scope', scopes.join(','));
      // Use user.id as state for regular Meta OAuth (not instagram_direct)
      authUrl.searchParams.set('state', `meta_${user.id}`);
      // Force re-request to ensure all pages are shown (including Business Manager pages)
      authUrl.searchParams.set('auth_type', 'rerequest');

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error connecting account:', error);
      toast.error('Nie udało się połączyć konta');
      setConnecting(false);
    }
  };

  const handleConnectInstagramDirect = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const { data: settings } = await supabase
        .from('user_settings')
        .select('instagram_direct_oauth_url')
        .eq('user_id', user.id)
        .maybeSingle();

      const customOAuthUrl = settings?.instagram_direct_oauth_url;

      if (!customOAuthUrl || !customOAuthUrl.trim()) {
        toast.error('Instagram Direct OAuth URL nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      // Ensure state=instagram_direct is in the OAuth URL
      const oauthUrl = new URL(customOAuthUrl);
      if (!oauthUrl.searchParams.has('state')) {
        oauthUrl.searchParams.set('state', 'instagram_direct');
      }

      localStorage.setItem('instagram_direct_return_url', window.location.href);

      window.location.href = oauthUrl.toString();
    } catch (error) {
      console.error('Error connecting Instagram Direct:', error);
      toast.error('Nie udało się połączyć konta Instagram Direct');
      setConnecting(false);
    }
  };

  const handleConnectYouTube = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const clientId = credentials.youtube_client_id;

      if (!clientId) {
        toast.error('YouTube Client ID nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/youtube-oauth`;

      const scopes = [
        'https://www.googleapis.com/auth/youtube.force-ssl',
        'https://www.googleapis.com/auth/youtube.readonly',
      ];

      localStorage.setItem('social_auth_redirect', window.location.href);

      const authUrl = new URL('https://accounts.google.com/o/oauth2/v2/auth');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scopes.join(' '));
      authUrl.searchParams.set('access_type', 'offline');
      authUrl.searchParams.set('prompt', 'consent');
      authUrl.searchParams.set('state', user.id);

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error connecting YouTube account:', error);
      toast.error('Nie udało się połączyć konta YouTube');
      setConnecting(false);
    }
  };

  const handleConnectTikTok = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const clientKey = credentials.tiktok_client_key;

      if (!clientKey) {
        toast.error('TikTok Client Key nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/tiktok-oauth`;

      // Only use scopes available in TikTok Login Kit
      // Note: Comment API is NOT available in public TikTok API
      const scopes = [
        'user.info.basic',  // User profile, avatar, display name
        'video.list',       // List user's public videos
      ];

      localStorage.setItem('social_auth_redirect', window.location.href);

      const authUrl = new URL('https://www.tiktok.com/v2/auth/authorize/');
      authUrl.searchParams.set('client_key', clientKey);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('scope', scopes.join(','));
      authUrl.searchParams.set('state', user.id);

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error connecting TikTok account:', error);
      toast.error('Nie udało się połączyć konta TikTok');
      setConnecting(false);
    }
  };

  const handleConnectTikTokBusiness = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const appId = credentials.tiktok_business_client_id;

      if (!appId) {
        toast.error('TikTok Business App ID nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/tiktok-business-oauth`;

      localStorage.setItem('social_auth_redirect', window.location.href);

      const authUrl = new URL('https://business-api.tiktok.com/portal/auth');
      authUrl.searchParams.set('app_id', appId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('state', user.id);

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error connecting TikTok Business account:', error);
      toast.error('Nie udało się połączyć konta TikTok Business');
      setConnecting(false);
    }
  };

  const handleConnectLinkedIn = async () => {
    setConnecting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;

      localStorage.setItem('social_auth_redirect', window.location.href);

      // Adres autoryzacji buduje funkcja brzegowa: ona zna aktualne scope'y i tylko ona
      // potrafi podpisac state, ktory zweryfikuje przy powrocie z LinkedIna.
      const response = await fetch(`${supabaseUrl}/functions/v1/linkedin-social-oauth`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
          Authorization: `Bearer ${session.access_token}`,
        },
      });

      const data = await response.json();

      if (!response.ok || !data.authUrl) {
        toast.error(
          data.error === 'no_credentials'
            ? 'LinkedIn Social Client ID nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.'
            : 'Nie udało się rozpocząć łączenia z LinkedIn'
        );
        setConnecting(false);
        return;
      }

      window.location.href = data.authUrl;
    } catch (error) {
      console.error('Error connecting LinkedIn account:', error);
      toast.error('Nie udało się połączyć konta LinkedIn');
      setConnecting(false);
    }
  };

  const handleConnectX = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const clientId = credentials.x_api_key;

      if (!clientId) {
        toast.error('X API Key nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/x-oauth`;

      localStorage.setItem('social_auth_redirect', window.location.href);

      const codeChallenge = 'challenge';
      const scopes = [
        'tweet.read',
        'tweet.write',
        'users.read',
        'offline.access',
      ];

      const authUrl = new URL('https://twitter.com/i/oauth2/authorize');
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', clientId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('state', user.id);
      authUrl.searchParams.set('scope', scopes.join(' '));
      authUrl.searchParams.set('code_challenge', codeChallenge);
      authUrl.searchParams.set('code_challenge_method', 'plain');

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error connecting X account:', error);
      toast.error('Nie udało się połączyć konta X');
      setConnecting(false);
    }
  };

  const handleConnectPinterest = async () => {
    setConnecting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        toast.error('Musisz być zalogowany');
        setConnecting(false);
        return;
      }

      const credentials = await getUserApiCredentials();
      const appId = credentials.pinterest_app_id;

      if (!appId) {
        toast.error('Pinterest App ID nie jest skonfigurowany. Dodaj swoje klucze API w ustawieniach.');
        setConnecting(false);
        return;
      }

      const supabaseUrl = import.meta.env.VITE_SUPABASE_URL;
      const redirectUri = `${supabaseUrl}/functions/v1/pinterest-oauth`;

      // Losowy state, sprawdzany po powrocie. Podpisywanie identyfikatora
      // użytkownika w state nie chroni przed niczym - to samo id widać w URL-u.
      const state = crypto.randomUUID();
      localStorage.setItem('pinterest_oauth_state', state);
      localStorage.setItem('social_auth_redirect', window.location.href);

      const scopes = [
        'user_accounts:read',
        'boards:read',
        'boards:write',
        'pins:read',
        'pins:write',
      ];

      const authUrl = new URL('https://www.pinterest.com/oauth/');
      authUrl.searchParams.set('response_type', 'code');
      authUrl.searchParams.set('client_id', appId);
      authUrl.searchParams.set('redirect_uri', redirectUri);
      authUrl.searchParams.set('state', state);
      authUrl.searchParams.set('scope', scopes.join(','));

      window.location.href = authUrl.toString();
    } catch (error) {
      console.error('Error connecting Pinterest account:', error);
      toast.error('Nie udało się połączyć konta Pinterest');
      setConnecting(false);
    }
  };

  const handleUpdatePollingInterval = async (accountId: string, intervalMinutes: number) => {
    try {
      const { error } = await supabase
        .from('social_accounts')
        .update({ polling_interval_minutes: intervalMinutes })
        .eq('id', accountId);

      if (error) throw error;

      toast.success(`Częstotliwość zmieniona na co ${intervalMinutes} minut`);
      loadAccounts();
    } catch (error) {
      console.error('Error updating polling interval:', error);
      toast.error('Nie udało się zaktualizować częstotliwości');
    }
  };

  const handleToggleAutoPublish = async (accountId: string, currentValue: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingSettings, error: checkError } = await supabase
        .from('social_settings')
        .select('id')
        .eq('account_id', accountId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingSettings) {
        const { error: updateError } = await supabase
          .from('social_settings')
          .update({ auto_publish_default_ready: !currentValue })
          .eq('id', existingSettings.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('social_settings')
          .insert({
            account_id: accountId,
            auto_publish_default_ready: !currentValue,
          });

        if (insertError) throw insertError;
      }

      toast.success(
        !currentValue
          ? 'Posty będą publikowane automatycznie'
          : 'Posty będą wymagały ręcznej akceptacji'
      );
      loadAccounts();
    } catch (error) {
      console.error('Error toggling auto publish:', error);
      toast.error('Nie udało się zaktualizować ustawienia');
    }
  };

  const handleToggleAutoComment = async (accountId: string, currentValue: boolean) => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: existingSettings, error: checkError } = await supabase
        .from('social_settings')
        .select('id')
        .eq('account_id', accountId)
        .maybeSingle();

      if (checkError) throw checkError;

      if (existingSettings) {
        const { error: updateError } = await supabase
          .from('social_settings')
          .update({ auto_comment_enabled: !currentValue })
          .eq('id', existingSettings.id);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('social_settings')
          .insert({
            account_id: accountId,
            auto_comment_enabled: !currentValue,
          });

        if (insertError) throw insertError;
      }

      toast.success(
        !currentValue
          ? 'Odpowiedzi na komentarze będą publikowane automatycznie'
          : 'Odpowiedzi na komentarze będą wymagały ręcznego zatwierdzenia'
      );
      loadAccounts();
    } catch (error) {
      console.error('Error toggling auto comment:', error);
      toast.error('Nie udało się zaktualizować ustawienia');
    }
  };

  const handleDelete = async (accountId: string) => {
    if (!confirm('Czy na pewno chcesz usunąć to konto? Wszystkie powiązane dane zostaną również usunięte.')) return;

    try {
      const { data, error } = await supabase
        .rpc('delete_social_account_cascade', { p_account_id: accountId });

      if (error) throw error;

      if (data && !data.success) {
        throw new Error(data.error || 'Unknown error');
      }

      toast.success('Konto zostało usunięte');
      loadAccounts();
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Nie udało się usunąć konta');
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'active':
        return <CheckCircle className="w-5 h-5 text-green-500" />;
      case 'expired':
        return <AlertCircle className="w-5 h-5 text-yellow-500" />;
      case 'error':
        return <XCircle className="w-5 h-5 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusText = (status: string) => {
    switch (status) {
      case 'active':
        return 'Aktywne';
      case 'expired':
        return 'Token wygasł';
      case 'error':
        return 'Błąd';
      default:
        return status;
    }
  };

  // Tokeny stron Facebooka i powiazanych kont Instagram Business nie maja daty
  // waznosci - Graph API nie zwraca dla nich `expires_in`. Zapisana data jest
  // tylko orientacyjna, a o waznosci decyduje cykliczna weryfikacja w Graph API
  // (funkcja validate_meta_tokens w bazie). Dlatego dla tych platform pokazujemy
  // wynik ostatniego sprawdzenia, a nie odliczanie do wymyslonej daty.
  const getApiVerifiedTokenInfo = (account: SocialAccount) => {
    if (account.platform !== 'facebook' && account.platform !== 'instagram') {
      return null;
    }

    const lastCheck = account.last_token_check ? new Date(account.last_token_check) : null;
    const isValid = account.status === 'active';

    return {
      isValid,
      lastCheck,
      statusColor: isValid ? 'text-green-400' : 'text-red-400',
      statusText: isValid ? 'Ważny' : 'Wymaga ponownego połączenia',
      errorMessage: account.account_data?.token_check?.error_message || null,
    };
  };

  const getTokenExpirationInfo = (account: SocialAccount) => {
    const tokenExpiresAt = account.token_expires_at;
    const metaTokenExpiresAt = account.meta_token_expires_at;

    if (!tokenExpiresAt && !metaTokenExpiresAt) {
      return null;
    }

    const primaryToken = tokenExpiresAt || metaTokenExpiresAt;
    if (!primaryToken) return null;

    const expirationDate = new Date(primaryToken);
    const now = new Date();
    const daysUntilExpiration = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    const hoursUntilExpiration = Math.floor((expirationDate.getTime() - now.getTime()) / (1000 * 60 * 60));

    let statusColor = 'text-green-400';
    let statusText = 'Ważny';
    let isAutoRefreshing = false;

    if (account.platform === 'youtube' || account.platform === 'tiktok' || account.platform === 'tiktok_business' || account.platform === 'linkedin' || account.platform === 'pinterest') {
      isAutoRefreshing = true;
      if (hoursUntilExpiration < 0) {
        statusColor = 'text-yellow-400';
        statusText = 'Odświeżanie...';
      } else {
        statusColor = 'text-green-400';
        statusText = 'Auto-odnawialny';
      }
    } else {
      if (daysUntilExpiration < 0) {
        statusColor = 'text-red-400';
        statusText = 'Wygasł';
      } else if (daysUntilExpiration <= 7) {
        statusColor = 'text-red-400';
        statusText = 'Wygasa wkrótce';
      } else if (daysUntilExpiration <= 14) {
        statusColor = 'text-yellow-400';
        statusText = 'Uwaga';
      }
    }

    return {
      date: expirationDate,
      daysUntilExpiration,
      hoursUntilExpiration,
      statusColor,
      statusText,
      hasMetaToken: !!metaTokenExpiresAt,
      isAutoRefreshing,
    };
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-[#7B4BFF]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <div className="mb-4">
          <h2 className="text-2xl font-bold text-white">Połączone konta</h2>
          <p className="text-gray-400 mt-1">
            Zarządzaj kontami Facebook, Instagram, YouTube, TikTok i LinkedIn
          </p>
        </div>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleConnectMeta}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-[#7B4BFF] text-white rounded-lg hover:bg-[#6A3FEF] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Plus className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">Konto Meta</span>
          </button>
          <button
            onClick={handleConnectInstagramDirect}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Instagram className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">Instagram Direct</span>
          </button>
          <button
            onClick={handleConnectYouTube}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Youtube className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">YouTube</span>
          </button>
          <button
            onClick={handleConnectTikTok}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Video className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">TikTok</span>
          </button>
          <button
            onClick={handleConnectTikTokBusiness}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Briefcase className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">TikTok Business</span>
          </button>
          <button
            onClick={handleConnectLinkedIn}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-[#0077B5] text-white rounded-lg hover:bg-[#006399] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Linkedin className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">LinkedIn</span>
          </button>
          <button
            onClick={handleConnectX}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-black text-white rounded-lg hover:bg-gray-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Twitter className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">X (Twitter)</span>
          </button>
          <button
            onClick={handleConnectPinterest}
            disabled={connecting}
            className="flex items-center justify-center space-x-2 px-4 py-3 bg-[#E60023] text-white rounded-lg hover:bg-[#C8001F] disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex-1 min-w-[200px] sm:flex-none sm:min-w-0"
          >
            {connecting ? (
              <RefreshCw className="w-5 h-5 animate-spin" />
            ) : (
              <Pin className="w-5 h-5" />
            )}
            <span className="whitespace-nowrap">Pinterest</span>
          </button>
        </div>
      </div>

      {accounts.length === 0 ? (
        <div className="space-y-6">
          <div className="bg-blue-900 border border-blue-700 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <AlertCircle className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
              <div className="text-sm text-blue-100">
                <p className="font-medium mb-2">Ważne: Konfiguracja webhook dla Facebook/Instagram</p>
                <p className="mb-2">
                  Po dodaniu kluczy API w ustawieniach, skonfiguruj aplikację w <a href="https://developers.facebook.com/apps" target="_blank" rel="noopener noreferrer" className="underline hover:text-blue-300">Facebook Developers Console</a>:
                </p>
                <ol className="list-decimal list-inside space-y-1 ml-2 text-blue-200">
                  <li>W <strong>Facebook Login → Settings</strong>, dodaj OAuth Redirect URI: <code className="bg-blue-950 px-1 py-0.5 rounded text-xs">https://sms.aiagenthub.pl/auth/meta/callback</code></li>
                  <li>W <strong>Webhooks</strong>, ustaw Callback URL: <code className="bg-blue-950 px-1 py-0.5 rounded text-xs">https://xahssxqgkrrlotaunwtg.supabase.co/functions/v1/social-inbound</code></li>
                  <li>Ustaw Verify Token: <code className="bg-blue-950 px-1 py-0.5 rounded text-xs">social_inbox_verify</code></li>
                  <li>Zasubskrybuj: <strong>feed</strong> i <strong>messages</strong></li>
                </ol>
                <p className="mt-2 text-blue-300 font-medium">
                  ⚠️ Nie używaj starego endpointa Vercel! Użyj tylko Supabase URL podanego powyżej.
                </p>
              </div>
            </div>
          </div>
          <div className="bg-gray-800 rounded-lg p-12 text-center">
            <div className="flex justify-center gap-4 mb-4">
              <Facebook className="w-16 h-16 text-gray-600" />
              <Youtube className="w-16 h-16 text-gray-600" />
              <Video className="w-16 h-16 text-gray-600" />
            </div>
            <h3 className="text-xl font-semibold text-white mb-2">
              Brak połączonych kont
            </h3>
            <p className="text-gray-400 mb-6">
              Połącz konto Meta (Facebook/Instagram), YouTube lub TikTok, aby rozpocząć odbieranie wiadomości i komentarzy
            </p>
            <div className="flex items-center justify-center gap-4">
            <button
              onClick={handleConnectMeta}
              disabled={connecting}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-[#7B4BFF] text-white rounded-lg hover:bg-[#6A3FEF] disabled:opacity-50 transition-colors"
            >
              <Plus className="w-5 h-5" />
              <span>Połącz Meta</span>
            </button>
            <button
              onClick={handleConnectInstagramDirect}
              disabled={connecting}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-gradient-to-r from-purple-600 to-pink-600 text-white rounded-lg hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 transition-colors"
            >
              <Instagram className="w-5 h-5" />
              <span>Instagram Direct</span>
            </button>
            <button
              onClick={handleConnectYouTube}
              disabled={connecting}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors"
            >
              <Youtube className="w-5 h-5" />
              <span>Połącz YouTube</span>
            </button>
            <button
              onClick={handleConnectTikTok}
              disabled={connecting}
              className="inline-flex items-center space-x-2 px-6 py-3 bg-black text-white rounded-lg hover:bg-gray-900 disabled:opacity-50 transition-colors"
            >
              <Video className="w-5 h-5" />
              <span>Połącz TikTok</span>
            </button>
          </div>
        </div>
        </div>
      ) : (
        <>
          <div className="mb-6">
            <h3 className="text-xl font-semibold text-white mb-4">Instagram Direct - Połącz konta</h3>
            <InstagramDirectLinking />
          </div>

          <div className="grid gap-4">
            {accounts.map((account) => (
            <div
              key={account.id}
              className="bg-gray-800 rounded-lg p-6 border border-gray-700 hover:border-[#7B4BFF] transition-colors"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="p-3 bg-gray-700 rounded-lg">
                    {account.platform === 'facebook' ? (
                      <Facebook className="w-6 h-6 text-blue-500" />
                    ) : account.platform === 'youtube' ? (
                      <Youtube className="w-6 h-6 text-red-500" />
                    ) : account.platform === 'tiktok' ? (
                      <Video className="w-6 h-6 text-white" />
                    ) : account.platform === 'tiktok_business' ? (
                      <Briefcase className="w-6 h-6 text-blue-400" />
                    ) : account.platform === 'linkedin' ? (
                      <Linkedin className="w-6 h-6 text-[#0077B5]" />
                    ) : account.platform === 'pinterest' ? (
                      <Pin className="w-6 h-6 text-[#E60023]" />
                    ) : (
                      <Instagram className="w-6 h-6 text-pink-500" />
                    )}
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="text-lg font-semibold text-white">
                        {account.page_name}
                      </h3>
                      {getStatusIcon(account.status)}
                    </div>
                    <div className="flex items-center space-x-4 text-sm text-gray-400">
                      <span className="capitalize">{account.platform === 'tiktok_business' ? 'TikTok Business' : account.platform}</span>
                      <span>•</span>
                      <span>{getStatusText(account.status)}</span>
                      {account.permissions && account.permissions.length > 0 && (
                        <>
                          <span>•</span>
                          <span>{account.permissions.length} uprawnień</span>
                        </>
                      )}
                    </div>
                    <div className="mt-2 text-xs text-gray-500">
                      Dodano: {new Date(account.created_at).toLocaleDateString('pl-PL')}
                    </div>
                    {(() => {
                      const apiInfo = getApiVerifiedTokenInfo(account);
                      if (apiInfo) {
                        return (
                          <div className="mt-2 p-2 bg-gray-900 rounded border border-gray-700">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-400">Token:</span>
                              <span className={`font-medium ${apiInfo.statusColor}`}>
                                {apiInfo.statusText}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs mt-1">
                              <span className="text-gray-400">Sprawdzono w Meta:</span>
                              <span className="font-medium text-gray-300">
                                {apiInfo.lastCheck
                                  ? `${apiInfo.lastCheck.toLocaleDateString('pl-PL')} ${apiInfo.lastCheck.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}`
                                  : 'jeszcze nie sprawdzono'}
                              </span>
                            </div>
                            {apiInfo.isValid ? (
                              <div className="mt-1 text-xs text-gray-500">
                                Token strony nie ma daty wygaśnięcia — ważność sprawdzamy w Meta co 15 minut
                              </div>
                            ) : (
                              <div className="mt-1 text-xs text-red-400">
                                {apiInfo.errorMessage || 'Meta odrzuciła token — połącz konto ponownie'}
                              </div>
                            )}
                          </div>
                        );
                      }

                      const tokenInfo = getTokenExpirationInfo(account);
                      if (tokenInfo) {
                        if (tokenInfo.isAutoRefreshing) {
                          return (
                            <div className="mt-2 p-2 bg-gray-900 rounded border border-gray-700">
                              <div className="flex items-center justify-between text-xs">
                                <span className="text-gray-400">Typ tokena:</span>
                                <span className="font-medium text-green-400">
                                  Auto-odnawialny (refresh token)
                                </span>
                              </div>
                              <div className="flex items-center justify-between text-xs mt-1">
                                <span className="text-gray-400">Status:</span>
                                <span className={`font-medium ${tokenInfo.statusColor}`}>
                                  {tokenInfo.statusText}
                                </span>
                              </div>
                              <div className="mt-1 text-xs text-gray-500">
                                Token jest automatycznie odswiezany co godzine
                              </div>
                            </div>
                          );
                        }
                        return (
                          <div className="mt-2 p-2 bg-gray-900 rounded border border-gray-700">
                            <div className="flex items-center justify-between text-xs">
                              <span className="text-gray-400">Token wygasa:</span>
                              <span className={`font-medium ${tokenInfo.statusColor}`}>
                                {tokenInfo.date.toLocaleDateString('pl-PL')} {tokenInfo.date.toLocaleTimeString('pl-PL', { hour: '2-digit', minute: '2-digit' })}
                              </span>
                            </div>
                            <div className="flex items-center justify-between text-xs mt-1">
                              <span className="text-gray-400">Status:</span>
                              <span className={`font-medium ${tokenInfo.statusColor}`}>
                                {tokenInfo.statusText}
                                {tokenInfo.daysUntilExpiration >= 0 && (
                                  <span className="ml-1">
                                    ({tokenInfo.daysUntilExpiration} {tokenInfo.daysUntilExpiration === 1 ? 'dzien' : 'dni'})
                                  </span>
                                )}
                              </span>
                            </div>
                            {tokenInfo.hasMetaToken && (
                              <div className="mt-1 text-xs text-blue-400">
                                Token Meta Ads
                              </div>
                            )}
                          </div>
                        );
                      }
                      return (
                        <div className="mt-2 p-2 bg-gray-900 rounded border border-gray-700">
                          <div className="text-xs text-gray-500">
                            Brak informacji o wygasnieciu tokena
                          </div>
                        </div>
                      );
                    })()}
                    {(account.platform === 'facebook' || account.platform === 'instagram' || account.platform === 'instagram_direct' || account.platform === 'linkedin') && (
                      <div className="mt-3 p-3 bg-gray-900 rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-white mb-1">
                              Auto-publikacja postów
                            </div>
                            <div className="text-xs text-gray-400">
                              {account.social_settings?.auto_publish_default_ready !== false
                                ? 'Posty są publikowane automatycznie po wygenerowaniu przez webhook'
                                : 'Posty wymagają ręcznej akceptacji przed publikacją'}
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleToggleAutoPublish(
                                account.id,
                                account.social_settings?.auto_publish_default_ready !== false
                              )
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7B4BFF] focus:ring-offset-2 focus:ring-offset-gray-900 ${
                              account.social_settings?.auto_publish_default_ready !== false
                                ? 'bg-[#7B4BFF]'
                                : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                account.social_settings?.auto_publish_default_ready !== false
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-white mb-1">
                              Auto-publikacja komentarzy
                            </div>
                            <div className="text-xs text-gray-400">
                              {account.social_settings?.auto_comment_enabled !== false
                                ? 'Odpowiedzi AI są publikowane automatycznie'
                                : 'Odpowiedzi AI wymagają ręcznego zatwierdzenia'}
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleToggleAutoComment(
                                account.id,
                                account.social_settings?.auto_comment_enabled !== false
                              )
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7B4BFF] focus:ring-offset-2 focus:ring-offset-gray-900 ${
                              account.social_settings?.auto_comment_enabled !== false
                                ? 'bg-[#7B4BFF]'
                                : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                account.social_settings?.auto_comment_enabled !== false
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    )}
                    {account.platform === 'youtube' && (
                      <div className="mt-3 p-3 bg-gray-900 rounded-lg border border-gray-700">
                        <div className="flex items-center justify-between mb-2">
                          <span className="text-xs font-medium text-gray-400">Częstotliwość sprawdzania</span>
                          <select
                            value={account.polling_interval_minutes || 15}
                            onChange={(e) => handleUpdatePollingInterval(account.id, parseInt(e.target.value))}
                            className="px-2 py-1 text-xs bg-gray-800 text-white border border-gray-600 rounded focus:ring-2 focus:ring-[#7B4BFF] focus:border-transparent"
                          >
                            <option value="15">Co 15 minut (oszczędne)</option>
                            <option value="30">Co 30 minut (zalecane)</option>
                            <option value="60">Co 60 minut (bezpieczne)</option>
                            <option value="120">Co 2 godziny</option>
                            <option value="240">Co 4 godziny</option>
                          </select>
                        </div>
                        <div className="text-xs text-gray-500">
                          <div className="flex items-center justify-between mb-1">
                            <span>Limit YouTube API:</span>
                            <span className="text-white">10,000 units/dzień</span>
                          </div>
                          {account.quota_usage_daily !== undefined && (
                            <div className="flex items-center justify-between">
                              <span>Dzisiejsze użycie:</span>
                              <span className={`font-medium ${
                                account.quota_usage_daily > 8000 ? 'text-red-400' :
                                account.quota_usage_daily > 5000 ? 'text-yellow-400' :
                                'text-green-400'
                              }`}>
                                ~{account.quota_usage_daily} units
                              </span>
                            </div>
                          )}
                        </div>
                        <div className="mt-2 text-xs text-gray-600">
                          Dzięki Activities API optymalizacji, używamy ~1-20 units na sprawdzenie zamiast ~200 units!
                        </div>
                        <div className="flex items-center justify-between mt-4 pt-4 border-t border-gray-700">
                          <div className="flex-1">
                            <div className="text-sm font-medium text-white mb-1">
                              Auto-publikacja komentarzy
                            </div>
                            <div className="text-xs text-gray-400">
                              {account.social_settings?.auto_comment_enabled !== false
                                ? 'Odpowiedzi AI są publikowane automatycznie'
                                : 'Odpowiedzi AI wymagają ręcznego zatwierdzenia'}
                            </div>
                          </div>
                          <button
                            onClick={() =>
                              handleToggleAutoComment(
                                account.id,
                                account.social_settings?.auto_comment_enabled !== false
                              )
                            }
                            className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-[#7B4BFF] focus:ring-offset-2 focus:ring-offset-gray-900 ${
                              account.social_settings?.auto_comment_enabled !== false
                                ? 'bg-[#7B4BFF]'
                                : 'bg-gray-600'
                            }`}
                          >
                            <span
                              className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                                account.social_settings?.auto_comment_enabled !== false
                                  ? 'translate-x-6'
                                  : 'translate-x-1'
                              }`}
                            />
                          </button>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleDelete(account.id)}
                    className="p-2 text-gray-400 hover:text-red-500 hover:bg-gray-700 rounded-lg transition-colors"
                    title="Usuń konto"
                  >
                    <Trash2 className="w-5 h-5" />
                  </button>
                </div>
              </div>
            </div>
          ))}
          </div>
        </>
      )}
    </div>
  );
}
